import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Percent, Wand2, X } from 'lucide-react';
import { Button } from '../Button';
import { fetchExternalAdvances, fetchProjectInvoices } from '../../services/invoiceService';
import {
    fetchRetentionBackfillState,
    upsertRetentionBackfill,
} from '../../services/retentionBackfill';
import { describeError } from '../../services/errorMessage';
import { formatCents, formatCentsPlain } from '../../services/money';
import {
    applySuggestion,
    backfillTotalCents,
    BackfillEdits,
    BackfillRow,
    buildBackfillRows,
    changedRows,
    hasSystemRetention,
    initialEdits,
    invalidRows,
} from './retentionBackfillLogic';

/**
 * Maske "Alt-Einbehalte erfassen" (Bauplan §16.2).
 *
 * Was der Auftraggeber auf Vorrechnungen bereits einbehalten hat, die selbst
 * keinen Einbehalt tragen — Belege vor dem Feature, Satzwechsel, externe
 * Alt-Abschlaege. Ausgestellte Belege sind eingefroren (Nr. 105), deshalb
 * steht der Betrag in einer eigenen Tabelle, je Vorrechnung ein Eintrag
 * ueber dieselbe Kennung wie im Abzugsblock. Der naechste Beleg rechnet ihn
 * als bereits einbehalten an, die Schlussrechnung gibt ihn frei.
 *
 * Laden und Speichern nach dem Muster von ExternalAdvancesPanel: alles auf
 * einmal laden, Fehler im Dialog stehen lassen (kein alert), nach dem
 * Speichern neu laden, damit der Bestand und nicht die Eingabe die Wahrheit
 * ist. Gespeichert wird nur, was sich gegenueber dem Bestand geaendert hat
 * (changedRows) — ein unveraendertes Speichern schreibt nichts.
 */
interface Props {
    projectId: string;
    /** Vereinbarter Satz des Projekts — Grundlage des Vorschlagsknopfs. */
    retentionPercent?: number;
    onClose: () => void;
    /**
     * Nach dem ersten erfolgreichen Speichern, einmal je Oeffnen: der Aufrufer
     * kann damit seinen Beleg neu rechnen lassen. Nicht je Speichervorgang,
     * denn das Neu-Rechnen kostet einen Roundtrip.
     */
    onSaved?: () => void;
}

const de = (iso?: string): string => (iso ? iso.split('-').reverse().join('.') : '—');

const inputClass =
    'w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50 disabled:text-slate-400';

export const RetentionBackfillModal: React.FC<Props> = ({
    projectId,
    retentionPercent,
    onClose,
    onSaved,
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    /** false: Tabelle fehlt (Nr. 107 nicht gelaufen) — Speichern gesperrt. */
    const [available, setAvailable] = useState(true);
    const [rows, setRows] = useState<BackfillRow[]>([]);
    const [edits, setEdits] = useState<BackfillEdits>({});
    const [savedOnce, setSavedOnce] = useState(false);

    const reload = async () => {
        setLoading(true);
        setError(null);
        try {
            const [invoices, externals, state] = await Promise.all([
                fetchProjectInvoices(projectId),
                fetchExternalAdvances(projectId),
                fetchRetentionBackfillState(projectId),
            ]);
            const next = buildBackfillRows(invoices, externals, state.entries);
            setAvailable(state.available);
            setRows(next);
            setEdits(initialEdits(next));
        } catch (e) {
            setError(describeError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const changes = useMemo(() => changedRows(rows, edits), [rows, edits]);
    const invalid = useMemo(() => invalidRows(rows, edits), [rows, edits]);
    const totalCents = useMemo(() => backfillTotalCents(rows, edits), [rows, edits]);
    const suggestable = rows.some((r) => !hasSystemRetention(r) && r.claimCents > 0);
    const canSave = available && !busy && !loading && changes.length > 0 && invalid.length === 0;

    const setEdit = (sourceRef: string, patch: Partial<BackfillEdits[string]>) => {
        setNotice(null);
        setEdits((prev) => ({
            ...prev,
            [sourceRef]: { ...(prev[sourceRef] ?? { amount: '', note: '' }), ...patch },
        }));
    };

    const save = async () => {
        if (!canSave) return;
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            // Nacheinander, nicht parallel: der Dienst legt je sourceRef genau
            // einen Eintrag an oder loescht ihn — zwei gleichzeitige Schreiber
            // auf dieselbe Tabelle brauchen wir hier nicht, und der Fehler
            // einer Zeile soll benennbar bleiben.
            for (const c of changes) {
                await upsertRetentionBackfill(projectId, c.entry);
            }
            const n = changes.length;
            setNotice(n === 1 ? 'Ein Alt-Einbehalt gespeichert.' : `${n} Alt-Einbehalte gespeichert.`);
            if (!savedOnce) {
                setSavedOnce(true);
                onSaved?.();
            }
            await reload();
        } catch (e) {
            setError(describeError(e));
        } finally {
            setBusy(false);
        }
    };

    const suggest = () => {
        if (!retentionPercent) return;
        setNotice(null);
        setEdits((prev) => applySuggestion(rows, prev, retentionPercent));
    };

    const percentLabel = retentionPercent
        ? `${retentionPercent.toLocaleString('de-DE')} %`
        : null;

    return (
        <div className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
                <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <Percent className="w-5 h-5 text-blue-400" />
                        Alt-Einbehalte erfassen
                    </h3>
                    <button onClick={onClose} className="hover:bg-white/10 p-1 rounded" title="Schließen">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto">
                    <div className="bg-blue-50 text-blue-800 p-4 rounded-xl text-sm border border-blue-100">
                        Was der Kunde auf diese Rechnungen bereits einbehalten hat. Wird beim
                        nächsten Beleg als bereits einbehalten angerechnet und mit der
                        Schlussrechnung freigegeben.
                    </div>

                    {!available && !loading && (
                        <div className="bg-amber-50 text-amber-800 p-4 rounded-xl text-sm border border-amber-200 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>
                                Die Tabelle für Alt-Einbehalte fehlt noch (Migration Nr. 107).
                                Bitte SQL ausführen.
                            </span>
                        </div>
                    )}

                    {loading ? (
                        <div className="p-6 text-center text-slate-400">
                            <Loader2 className="w-5 h-5 animate-spin inline" />
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                    Vorrechnungen des Projekts
                                </span>
                                {/* Vorschlag nur fuer Zeilen OHNE Systemeinbehalt:
                                    wer im System einbehalten hat, braucht keinen
                                    Alt-Einbehalt — sonst zaehlte der Betrag doppelt. */}
                                {percentLabel && suggestable && (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={suggest}
                                        disabled={busy || !available}
                                        icon={<Wand2 className="w-4 h-4" />}
                                        title="Projektsatz auf die Forderung der Zeilen ohne Systemeinbehalt rechnen"
                                    >
                                        {percentLabel} vorbelegen
                                    </Button>
                                )}
                            </div>

                            <div className="overflow-x-auto rounded-xl border border-slate-200">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs">
                                        <tr>
                                            <th className="p-3">Nr.</th>
                                            <th className="p-3">Datum</th>
                                            <th className="p-3 text-right">Forderung brutto</th>
                                            <th className="p-3 text-right">im System einbehalten</th>
                                            <th className="p-3 text-right w-36">Alt-Einbehalt €</th>
                                            <th className="p-3">Notiz</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {rows.map((r) => {
                                            const edit = edits[r.sourceRef] ?? { amount: '', note: '' };
                                            const broken = invalid.includes(r);
                                            const systemHeld = hasSystemRetention(r);
                                            return (
                                                <tr key={r.sourceRef} className={systemHeld ? 'text-slate-400' : ''}>
                                                    <td className="p-3 font-mono">
                                                        {r.invoiceNumber}
                                                        {r.external && (
                                                            <span className="ml-2 text-[11px] font-bold uppercase bg-amber-50 text-amber-600 rounded px-2 py-0.5 font-sans">
                                                                extern
                                                            </span>
                                                        )}
                                                        {r.claimCents === 0 && r.existing && (
                                                            <div className="text-[11px] text-amber-700 font-sans">
                                                                Vorrechnung nicht mehr vorhanden — Eintrag prüfen oder leeren.
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="p-3 text-slate-500 whitespace-nowrap">{de(r.invoiceDate)}</td>
                                                    <td className="p-3 text-right font-mono whitespace-nowrap">
                                                        {formatCentsPlain(r.claimCents)}
                                                    </td>
                                                    <td className="p-3 text-right font-mono whitespace-nowrap">
                                                        {r.systemRetentionCents == null
                                                            ? '—'
                                                            : formatCentsPlain(r.systemRetentionCents)}
                                                    </td>
                                                    <td className="p-3">
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={edit.amount}
                                                            disabled={!available || busy}
                                                            placeholder="keiner"
                                                            title={
                                                                systemHeld
                                                                    ? 'Dieser Beleg hat im System bereits einbehalten — ein Alt-Einbehalt käme obendrauf.'
                                                                    : 'Betrag in Euro, den der Kunde auf diese Rechnung einbehalten hat'
                                                            }
                                                            onChange={(e) => setEdit(r.sourceRef, { amount: e.target.value })}
                                                            className={`${inputClass} text-right font-mono ${broken ? 'border-red-400 focus:ring-red-400' : ''}`}
                                                        />
                                                    </td>
                                                    <td className="p-3">
                                                        <input
                                                            type="text"
                                                            value={edit.note}
                                                            disabled={!available || busy}
                                                            placeholder="optional"
                                                            onChange={(e) => setEdit(r.sourceRef, { note: e.target.value })}
                                                            className={inputClass}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {rows.length === 0 && (
                                            <tr>
                                                <td colSpan={6} className="p-6 text-center text-slate-400 italic">
                                                    Keine Vorrechnungen — es gibt nichts, worauf ein Alt-Einbehalt liegen könnte.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    {rows.length > 0 && (
                                        <tfoot>
                                            <tr className="bg-slate-50 font-semibold text-slate-700">
                                                <td className="p-3" colSpan={4}>Alt-Einbehalte gesamt</td>
                                                <td className="p-3 text-right font-mono whitespace-nowrap">
                                                    {formatCents(totalCents)}
                                                </td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>

                            <p className="text-[11px] text-slate-400">
                                Leer heißt: kein Alt-Einbehalt. Zeilen mit Systemeinbehalt sind
                                ausgegraut — dort hat der Beleg selbst einbehalten. Änderungen wirken
                                nur auf künftige Belege.
                            </p>
                        </>
                    )}

                    {invalid.length > 0 && (
                        <p className="text-xs text-red-600 flex items-start gap-1">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            Bitte den Betrag als Euro mit Komma angeben (z. B. 2.366,56) — rot
                            markierte Felder sind nicht lesbar.
                        </p>
                    )}
                    {error && (
                        <p className="text-xs text-red-600 flex items-start gap-1">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            {error}
                        </p>
                    )}
                    {notice && (
                        <p className="text-xs text-emerald-700 flex items-start gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            {notice}
                        </p>
                    )}
                </div>

                <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2 shrink-0">
                    <Button variant="secondary" onClick={onClose} disabled={busy}>
                        Schließen
                    </Button>
                    <Button
                        onClick={() => void save()}
                        disabled={!canSave}
                        title={
                            !available
                                ? 'Speichern gesperrt, bis Migration Nr. 107 gelaufen ist'
                                : changes.length === 0
                                  ? 'Nichts geändert'
                                  : undefined
                        }
                    >
                        {busy ? 'Speichert…' : changes.length > 0 ? `Speichern (${changes.length})` : 'Speichern'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
